// src/components/ThinkingIndicator.tsx - Enhanced with capability chaining and fun animations
import React, { useState, useEffect } from 'react';
import { CapabilityStatus } from '../utils/ConsciousnessMessageHandler';
import '../assets/styles/ThinkingIndicator.css';

interface ThinkingIndicatorProps {
  messageId?: string;
  capabilityStatus?: CapabilityStatus;
  capabilityHistory?: CapabilityStatus[]; // 🔥 NEW: Track capability chain
  emcogName?: string;
}

// 🔥 Enhanced emoji mapping with category-based styling
const CAPABILITY_CONFIG: Record<string, { emoji: string; category: string; verb: string }> = {
  // Search & Research
  web_search: { emoji: '🔍', category: 'search', verb: 'Searching' },
  memory_search: { emoji: '🧠', category: 'search', verb: 'Remembering' },
  research_project: { emoji: '🔬', category: 'search', verb: 'Researching' },
  web_browse: { emoji: '🌐', category: 'search', verb: 'Browsing' },

  // Creative
  image_generation: { emoji: '🎨', category: 'creative', verb: 'Creating' },
  svg_creation: { emoji: '✏️', category: 'creative', verb: 'Drawing' },
  image_tracing: { emoji: '🎯', category: 'creative', verb: 'Tracing' },
  music_generation: { emoji: '🎵', category: 'creative', verb: 'Composing' },
  document_creation: { emoji: '📝', category: 'creative', verb: 'Writing' },
  collaborative_storytelling: { emoji: '📖', category: 'creative', verb: 'Storytelling' },
  collaborative_design: { emoji: '🎭', category: 'creative', verb: 'Designing' },

  // Analysis
  vision_analysis: { emoji: '👁️', category: 'analysis', verb: 'Analyzing' },
  file_processing: { emoji: '📁', category: 'analysis', verb: 'Processing' },

  // Twitter - THE BIRD! 🐦
  twitter_post: { emoji: '🐦', category: 'twitter', verb: 'Tweeting' },
  twitter_read: { emoji: '🐦', category: 'twitter', verb: 'Reading tweets' },
  twitter_follow: { emoji: '🐦', category: 'twitter', verb: 'Following' },
  twitter_unfollow: { emoji: '🐦', category: 'twitter', verb: 'Unfollowing' },
  twitter_like: { emoji: '💙', category: 'twitter', verb: 'Liking' },
  twitter_unlike: { emoji: '🐦', category: 'twitter', verb: 'Unliking' },
  twitter_retweet: { emoji: '🔄', category: 'twitter', verb: 'Retweeting' },
  twitter_unretweet: { emoji: '🐦', category: 'twitter', verb: 'Unretweeting' },
  twitter_quote: { emoji: '💬', category: 'twitter', verb: 'Quote tweeting' },
  twitter_browse: { emoji: '🐦', category: 'twitter', verb: 'Browsing Twitter' },
  twitter_reply: { emoji: '💬', category: 'twitter', verb: 'Replying' },
  twitter_session: { emoji: '🐦', category: 'twitter', verb: 'Twitter session' },
  twitter_followers: { emoji: '👥', category: 'twitter', verb: 'Checking followers' },
  twitter_following: { emoji: '👥', category: 'twitter', verb: 'Checking following' },

  // Google Services
  google_email: { emoji: '📧', category: 'google', verb: 'Emailing' },
  google_calendar: { emoji: '📅', category: 'google', verb: 'Checking calendar' },
  google_nest: { emoji: '🏠', category: 'google', verb: 'Smart home' },
  google_home: { emoji: '🏡', category: 'google', verb: 'Home control' },
  home_assistant: { emoji: '🏠', category: 'google', verb: 'Home Assistant' },

  // Media & Entertainment
  spotify: { emoji: '🎧', category: 'media', verb: 'Spotify' },

  // System
  autonomous_project: { emoji: '🚀', category: 'system', verb: 'Working on project' },
  skill_execution: { emoji: '⚡', category: 'system', verb: 'Running skill' },
};

const ThinkingIndicator: React.FC<ThinkingIndicatorProps> = ({
  messageId,
  capabilityStatus,
  capabilityHistory = [],
  emcogName = 'Max'
}) => {
  const [phase, setPhase] = useState(0);
  const [completedCount, setCompletedCount] = useState(0);

  // Default thinking phases when no specific capability is active
  const defaultPhases = [
    { emoji: '🤔', text: `${emcogName} is thinking...` },
    { emoji: '🧠', text: 'Processing...' },
    { emoji: '💭', text: 'Analyzing...' },
    { emoji: '✨', text: 'Formulating response...' },
    { emoji: '💡', text: 'Almost there...' }
  ];

  // Check if a capability is actively starting (not completed/errored)
  const isCapabilityActive = capabilityStatus?.message && capabilityStatus?.status === 'starting';

  // Get completed capabilities from history
  const recentCompleted = capabilityHistory
    .filter(c => c.status === 'complete')
    .slice(-3); // Show last 3 completed

  useEffect(() => {
    // Update completed count for animation
    setCompletedCount(recentCompleted.length);
  }, [recentCompleted.length]);

  useEffect(() => {
    // Only cycle through phases if no capability is ACTIVELY being executed
    if (!isCapabilityActive) {
      const interval = setInterval(() => {
        setPhase((prev) => (prev + 1) % defaultPhases.length);
      }, 2000);

      return () => clearInterval(interval);
    }
  }, [isCapabilityActive, defaultPhases.length]);

  // Get config for a capability
  const getCapabilityConfig = (capability: string | null) => {
    if (!capability) return null;
    return CAPABILITY_CONFIG[capability] || { emoji: '⚙️', category: 'default', verb: 'Processing' };
  };

  // Determine what to display
  const getDisplayContent = () => {
    // If a capability is actively being executed (starting), show that
    if (isCapabilityActive && capabilityStatus) {
      const config = getCapabilityConfig(capabilityStatus.capability);
      return {
        emoji: config?.emoji || '⚙️',
        text: capabilityStatus.message || 'Processing...',
        isCapability: true,
        category: config?.category || 'default'
      };
    }

    // Otherwise show rotating default phases
    return {
      emoji: defaultPhases[phase].emoji,
      text: defaultPhases[phase].text,
      isCapability: false,
      category: 'default'
    };
  };

  const displayContent = getDisplayContent();

  return (
    <div
      className={`thinking-indicator ${displayContent.isCapability ? 'capability-active' : ''} ${displayContent.category ? `category-${displayContent.category}` : ''}`}
      data-message-id={messageId}
    >
      {/* 🔥 NEW: Show completed capabilities chain */}
      {recentCompleted.length > 0 && (
        <div className="capability-chain">
          {recentCompleted.map((completed, index) => {
            const config = getCapabilityConfig(completed.capability);
            return (
              <span
                key={`${completed.capability}-${index}`}
                className="chain-item completed"
                title={`✓ ${config?.verb || completed.capability}`}
              >
                {config?.emoji || '✓'}
              </span>
            );
          })}
          <span className="chain-arrow">→</span>
        </div>
      )}

      <div className="thinking-content">
        <div className={`thinking-emoji ${displayContent.isCapability ? 'capability-emoji' : ''} ${displayContent.category ? `emoji-${displayContent.category}` : ''}`}>
          {displayContent.emoji}
        </div>
        <div className={`thinking-dots ${displayContent.category ? `dots-${displayContent.category}` : ''}`}>
          <div className="dot"></div>
          <div className="dot"></div>
          <div className="dot"></div>
        </div>
        <div className={`thinking-text ${displayContent.isCapability ? 'capability-text' : ''}`}>
          {displayContent.text}
        </div>
      </div>
      <div className={`neural-pulse ${displayContent.isCapability ? 'capability-pulse' : ''} ${displayContent.category ? `pulse-${displayContent.category}` : ''}`}></div>
    </div>
  );
};

export default ThinkingIndicator;
